import { createSelector } from 'reselect';

import { getAdvices } from 'works/utils';
import { getEntity } from 'modules/entity/selectors';
import { getSelectedChecklistGroupId } from 'modules/works/selectors';
import { ADVICE_STATUS, CHECKLIST_TYPE } from 'modules/entity/constants';
import { getAcceptedAdvices, getPendingAdvices } from 'modules/works/selectors/advices';
import { CHECKLIST_GROUP_STATUS, PAYMENT_STATUS, PAYMENT_TYPE } from 'modules/entity/subEntities/intervention/constants';

import type { ChecklistGroup } from 'modules/entity/subEntities/intervention/types';
import type { AdviceStatusEnum } from 'types/Advice';
import type { MayBe } from 'types/common';

import { getUI, isSales } from './index';

const getProps = (_state: unknown, props: number) => props;

const getPayOnline = createSelector(getUI, ui => ui.payOnline);
const getPayDeposit = createSelector(getUI, ui => ui.payDeposit);
const getFinalInvoice = createSelector(getUI, ui => ui.finalInvoice);

export const isLoadingPayOnline = createSelector(getPayOnline, payOnline => payOnline.isLoading);
export const isLoadingPayDeposit = createSelector(getPayDeposit, payDeposit => payDeposit.isLoading);
export const isPayingFinalInvoice = createSelector(getFinalInvoice, finalInvoice => finalInvoice.isPaying);

export const isLoading = createSelector(
  isLoadingPayOnline,
  isLoadingPayDeposit,
  (payOnline, payDeposit) => payOnline || payDeposit
);

const getDepositPercentages = createSelector(
  isSales,
  getPendingAdvices,
  getAcceptedAdvices,
  (isSalesMode, pendingAdvices, acceptedAdvices) => {
    if (isSalesMode) {
      // In sales, we can have different deposit percentage, since the client can accept multiple "batches" of advices before paying.
      const uniquePercentages = acceptedAdvices.reduce((acc, { status, depositPercentage }) => {
        if (status === ADVICE_STATUS.ACCEPTED && depositPercentage && depositPercentage > 0) {
          acc.add(depositPercentage);
        }

        return acc;
      }, new Set<number>());

      return Array.from(uniquePercentages);
    }

    // In after-sales, all the pending advices have the same deposit percentage due to force online payment.
    return pendingAdvices.length > 0 ? [pendingAdvices[0].depositPercentage ?? 0] : [];
  }
);

export const getDepositPercentage = createSelector(getDepositPercentages, percentages => percentages[0] ?? 0);

const getPaymentsData = createSelector(getEntity, ({ payments = [] }) => payments);

export const getPayments = createSelector(getPaymentsData, (payments) =>
  payments.filter(
    ({ status, type }) =>
      type !== PAYMENT_TYPE.FINAL_INVOICE && (status === PAYMENT_STATUS.SUCCESS ||
      status === PAYMENT_STATUS.PRE_AUTHORIZED ||
      status === PAYMENT_STATUS.FAILED),
  ));

export const isPaymentPending = createSelector(getPaymentsData, (payments) =>
  payments.some(({ status }) => status === PAYMENT_STATUS.PENDING));

export const getTotalPaid = createSelector(getPayments, (payments) =>
  payments.reduce((acc, payment) => {
    if (payment.status === PAYMENT_STATUS.SUCCESS || payment.status === PAYMENT_STATUS.PRE_AUTHORIZED) {
      return acc + (payment.amount ?? 0);
    }
    return acc;
  }, 0));

const hasAcceptedAdvices = (groupOffer: MayBe<string>, checklistGroups: Array<ChecklistGroup>) =>
  !groupOffer ||
  checklistGroups.some(({ id, status }) => id === groupOffer && status === CHECKLIST_GROUP_STATUS.ACCEPTED);

export const canPayOnline = createSelector(
  getEntity,
  getSelectedChecklistGroupId,
  ({ checklistGroups = [], payOnlineUrl, payAtDealershipUrl }, selectedGroupOfferId) =>
    Boolean(payOnlineUrl || payAtDealershipUrl) && hasAcceptedAdvices(selectedGroupOfferId, checklistGroups)
);

export const canPayDeposit = createSelector(
  getEntity,
  getSelectedChecklistGroupId,
  ({ checklistGroups = [], payDepositUrl }, selectedGroupOfferId) =>
    payDepositUrl && hasAcceptedAdvices(selectedGroupOfferId, checklistGroups)
);

export const getPayOnlineAdvices = createSelector(getEntity, ({ checklists, forceOnlinePayment }) => {
  const allAdvices = getAdvices({ checklists });
  const hasDepositPaid = allAdvices.some(({ status }) => status === ADVICE_STATUS.DEPOSIT_PAID);
  const hasOnlyDoneWorks = allAdvices.every(({ checklistType }) => checklistType === CHECKLIST_TYPE.DONE);
  const canForceOnlinePayment = forceOnlinePayment && !hasDepositPaid && !hasOnlyDoneWorks;

  if (!canForceOnlinePayment) {
    return null;
  }

  type SelectedAdvice = { adviceId: string; version: number; status: AdviceStatusEnum };
  const advices = allAdvices.reduce<Array<SelectedAdvice>>((acc, { checklistType, adviceId, version, status }) => {
    if (checklistType !== CHECKLIST_TYPE.DONE) {
      acc.push({ adviceId, version, status });
    }
    return acc;
  }, []);

  return advices.length > 0 ? advices : null;
});

const getMinPayment = createSelector(getEntity, ({ config }) => config?.minPayment);
const getMaxPayment = createSelector(getEntity, ({ config }) => config?.maxPayment);

const isAmountTooLow = createSelector(getMinPayment, getProps, (minPayment, amount) => amount < (minPayment ?? Number.NEGATIVE_INFINITY));
const isAmountTooHigh = createSelector(getMaxPayment, getProps, (maxPayment, amount) => amount > (maxPayment ?? Number.POSITIVE_INFINITY));
export const canPayAmount = createSelector(isAmountTooLow, isAmountTooHigh, (tooLow, tooHigh) => !tooLow && !tooHigh);

export const getAmountsError = createSelector(
  canPayAmount,
  isAmountTooLow,
  isAmountTooHigh,
  (isAvailable, isTooLow, isTooHigh) => {
    if (!isAvailable) {
      return [
        ...(isTooLow
          ? [{ id: 'web.online-payment.error.low', defaultMessage: 'The amount to pay is insufficient' }]
          : []),
        ...(isTooHigh
          ? [{ id: 'web.online-payment.error.high', defaultMessage: 'The amount to pay is too high' }]
          : []),
      ];
    }
    return undefined;
  }
);

export const getPaymentMethods = createSelector(
  getEntity,
  ({ paymentMethods = {} }) => paymentMethods?.methods ?? []
);
